import { useMemo, useState } from "react";
import { Search, ChevronRight, Mic, Headphones, FileText, Sparkles } from "lucide-react";
import type { CaseDetail } from "./types";
import { classColor, classLabel } from "./Charts";

type SortKey = "saved" | "date" | "officer" | "words";

type Props = {
  cases: CaseDetail[];
  onSelect: (id: string) => void;
  obscureAudioSplit?: boolean;
};

function audioMin(c: CaseDetail) {
  return Math.round((c.narrative_sec + c.interaction_sec) / 60);
}

// Typed cases get a non-zero saved_min from the snapshot SQL (setup + words/wpm
// formula runs for every case), so sorting/summing on the raw value would float
// wordy typed reports above genuinely AI-assisted ones. Credit savings only
// where the case was actually AI-assisted.
function effectiveSavedMin(c: CaseDetail) {
  return c.ai_assisted ? c.saved_min || 0 : 0;
}

function classIcon(c: CaseDetail["class"]) {
  if (c === "narrative") return <Mic size={12} />;
  if (c === "interaction" || c === "mixed") return <Headphones size={12} />;
  return <FileText size={12} />;
}

// When obscured, audio-driven workflows (narrative/interaction/mixed) are
// reported as a single generic "AI-assisted" badge so the case index doesn't
// expose which audio type was used.
function obscuredClassLabel(c: CaseDetail["class"]): string {
  if (c === "narrative" || c === "interaction" || c === "mixed") {
    return "AI-assisted";
  }
  return classLabel(c);
}

function obscuredClassColor(c: CaseDetail["class"]): string {
  if (c === "narrative" || c === "interaction" || c === "mixed") {
    return "#1a78c2";
  }
  return classColor(c);
}

function obscuredClassIcon(c: CaseDetail["class"]) {
  if (c === "narrative" || c === "interaction" || c === "mixed") {
    return <Sparkles size={12} />;
  }
  return classIcon(c);
}

export function CaseList({ cases, onSelect, obscureAudioSplit = false }: Props) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("saved");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [classFilter, setClassFilter] = useState<string>("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = cases;
    if (q) {
      rows = rows.filter(
        (c) =>
          (c.officer_name || "").toLowerCase().includes(q) ||
          (c.badge_id || "").toLowerCase().includes(q) ||
          (c.case_number || "").toLowerCase().includes(q)
      );
    }
    if (classFilter !== "all") {
      // When obscured, "audio_ai" filter selects all three underlying audio
      // workflows since the user can no longer distinguish between them.
      if (obscureAudioSplit && classFilter === "audio_ai") {
        rows = rows.filter(
          (c) => c.class === "narrative" || c.class === "interaction" || c.class === "mixed"
        );
      } else {
        rows = rows.filter((c) => c.class === classFilter);
      }
    }
    const dir = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (sortKey) {
        case "saved":
          return dir * (effectiveSavedMin(a) - effectiveSavedMin(b));
        case "date":
          return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        case "officer":
          return dir * (a.officer_name || "").localeCompare(b.officer_name || "");
        case "words":
          return dir * ((a.words || 0) - (b.words || 0));
        default:
          return 0;
      }
    });
  }, [cases, query, sortKey, sortDir, classFilter, obscureAudioSplit]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir(key === "officer" ? "asc" : "desc");
    }
  };

  const totalSaved = filtered.reduce((s, c) => s + effectiveSavedMin(c), 0);

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by officer, badge, or case number"
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white"
          />
        </div>
        <select
          value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}
          className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All workflows</option>
          {obscureAudioSplit ? (
            <>
              <option value="audio_ai">AI-assisted</option>
              <option value="enhanced_writing">Enhanced writing</option>
              <option value="ai_text">AI-assisted text</option>
              <option value="manual_typed">Typed</option>
            </>
          ) : (
            <>
              <option value="narrative">Narrative dictation</option>
              <option value="interaction">Interaction / interview</option>
              <option value="mixed">Mixed audio</option>
              <option value="enhanced_writing">Enhanced writing</option>
              <option value="ai_text">AI-assisted text</option>
              <option value="manual_typed">Typed</option>
            </>
          )}
        </select>
        <div className="flex gap-2 text-xs">
          <SortButton active={sortKey === "saved"} dir={sortDir} onClick={() => toggleSort("saved")}>
            Saved
          </SortButton>
          <SortButton active={sortKey === "date"} dir={sortDir} onClick={() => toggleSort("date")}>
            Date
          </SortButton>
          <SortButton active={sortKey === "words"} dir={sortDir} onClick={() => toggleSort("words")}>
            Words
          </SortButton>
          <SortButton active={sortKey === "officer"} dir={sortDir} onClick={() => toggleSort("officer")}>
            Officer
          </SortButton>
        </div>
      </div>

      <div className="text-xs text-slate-500 mb-2 flex items-center gap-3 flex-wrap">
        <span>
          Showing <span className="font-semibold text-slate-700">{filtered.length}</span>
          {(query || classFilter !== "all") && ` of ${cases.length}`} reports
        </span>
        <span className="text-slate-300">·</span>
        <span>
          <span className="font-semibold text-slate-700">
            {filtered.filter((c) => c.ai_assisted).length}
          </span>{" "}
          AI-assisted ·{" "}
          <span className="font-semibold text-slate-700">
            {filtered.filter((c) => !c.ai_assisted).length}
          </span>{" "}
          typed
        </span>
        <span className="text-slate-300">·</span>
        <span>
          Saved in view{" "}
          <span className="font-semibold text-emerald-600">
            {(totalSaved / 60).toFixed(1)} hr
          </span>
        </span>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="max-h-[560px] overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-6 py-12 text-center text-slate-500 text-sm">No cases match.</div>
          )}
          {filtered.map((c, idx) => {
            const audio = audioMin(c);
            return (
              <button
                key={c.id}
                onClick={() => onSelect(c.id)}
                className={`w-full text-left flex items-center gap-4 px-4 py-3 hover:bg-blue-50/50 transition-colors border-b border-slate-100 last:border-b-0 group ${
                  idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                }`}
              >
                <div
                  className="shrink-0 w-14 h-14 rounded-lg flex flex-col items-center justify-center font-bold text-sm"
                  style={{
                    backgroundColor: c.ai_assisted
                      ? "rgba(16,185,129,0.08)"
                      : "rgba(100,116,139,0.10)",
                    color: c.ai_assisted ? "#047857" : "#64748b",
                  }}
                >
                  {c.ai_assisted ? (
                    <>
                      <span className="text-base tabular-nums">{Math.round(c.saved_min)}</span>
                      <span className="text-[9px] uppercase tracking-wider opacity-60">min</span>
                    </>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider opacity-70">typed</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-slate-900 truncate">
                      {c.officer_name}
                    </span>
                    {c.badge_id && (
                      <span className="text-xs text-slate-400 shrink-0">#{c.badge_id}</span>
                    )}
                    <span
                      className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full shrink-0"
                      style={{
                        backgroundColor:
                          (obscureAudioSplit ? obscuredClassColor(c.class) : classColor(c.class)) +
                          "20",
                        color: obscureAudioSplit
                          ? obscuredClassColor(c.class)
                          : classColor(c.class),
                      }}
                    >
                      {obscureAudioSplit ? obscuredClassIcon(c.class) : classIcon(c.class)}
                      {obscureAudioSplit ? obscuredClassLabel(c.class) : classLabel(c.class)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                    <span>
                      {new Date(c.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "2-digit",
                      })}
                    </span>
                    <span>
                      <span className="font-semibold text-slate-700">{c.words.toLocaleString()}</span> words
                    </span>
                    {audio > 0 && (
                      <span>
                        <span className="font-semibold text-slate-700">{audio} min</span> audio
                      </span>
                    )}
                    {c.case_number && (
                      <span className="text-slate-400">· {c.case_number}</span>
                    )}
                  </div>
                </div>
                <ChevronRight
                  size={18}
                  className="text-slate-300 group-hover:text-blue-500 shrink-0 transition-colors"
                />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SortButton({
  active,
  dir,
  onClick,
  children,
}: {
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-md border transition-colors ${
        active
          ? "border-blue-500 bg-blue-50 text-blue-700 font-semibold"
          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      {children} {active && <span className="ml-0.5">{dir === "asc" ? "↑" : "↓"}</span>}
    </button>
  );
}
